/**
 * The FinderlyFix-hosted widget pages (`finderlyfix.com/p/{slug}`, FR-018,
 * T063, #873 D-1/D-3; migration 085): up to 3 per organisation (e.g. one per
 * brand, service area or campaign), each with its own link, QR code, on/off
 * switch, headline, intro and live site.
 *
 * Mounted in two places: onboarding screen 3 ("We have no website we
 * control", InstallOwner.tsx), where the page is created, and Sites & keys,
 * where a partner comes back later to change the copy — the hosted page is
 * the website that stands in for theirs, so it sits with the other places the
 * widget runs rather than under Widget settings (whose config it does not
 * touch: logo and colours come from the PUBLISHED widget config).
 *
 * The rules are the API's (contracts/partner-api.md §Hosted page):
 *   * the address (slug) can be chosen at creation only — the API has no way
 *     to change it (`SLUG_IMMUTABLE`), because it is printed on flyers — so
 *     the create form says so, and the editor shows it as fixed text;
 *   * slug `^[a-z0-9]([a-z0-9-]{0,46}[a-z0-9])?$` (≤ 48), `SLUG_TAKEN` when
 *     another partner has it; headline ≤ 120, intro ≤ 600 plain text; an
 *     empty field is stored as "none" and the page falls back to default copy;
 *   * the public page is cached for 60 s, so an edit can take a minute to show;
 *   * `HOSTED_PAGE_LIMIT` past the organisation's limit (`limit` in the list);
 *     "Remove" (DELETE) switches a page off and takes it off the list, which
 *     frees a place — nothing is deleted and the address stays the
 *     organisation's.
 *
 * Each card links to `/pages/:slug` (HostedPageProfile.tsx: phone, services,
 * hours, photos), with the profile's completeness score for an enabled page.
 */
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { ApiError, type FieldIssue } from '../api/errors';
import type { PartnerApi } from '../api/partner';
import type { HostedPage, HostedPageCreateBody, HostedPageList, SiteSummary } from '../api/types';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import { PageCompletenessLink } from '../hostedPage/Completeness';
import { ErrorNotice, Field, Loading, Switch, Time, useToast } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';
import { WidgetPageLink } from './WidgetPageShare';

/** server/src/services/hosted-page.ts — keep in step. */
export const HOSTED_PAGE_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,46}[a-z0-9])?$/;
export const HOSTED_PAGE_SLUG_MAX = 48;
export const HOSTED_PAGE_HEADLINE_MAX = 120;
export const HOSTED_PAGE_INTRO_MAX = 600;

/** The address the server mints from the display name (`slugifyDisplayName`), shown as the default. */
export function suggestSlug(displayName: string): string {
  const folded = displayName
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  // `hostedPageSlugBase`: brand words are reserved (SLUG_RESERVED), so the
  // server takes them out of the name before minting.
  const base = folded
    .slice(0, HOSTED_PAGE_SLUG_MAX - 7)
    .replace(/finderlyfix|finderly/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');
  return base && base !== 'partner' ? base : 'partner-page';
}

/** The server's MAX_HOSTED_PAGES_PER_ORG; the list's `limit` wins when present. */
export const HOSTED_PAGES_MAX = 3;

/** The deprecated `GET /partner/hosted-page` is a 404 until a page exists; that is "none yet", not an error. */
export async function hostedPageOrNull(get: () => Promise<HostedPage>): Promise<HostedPage | null> {
  try {
    return await get();
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/**
 * The organisation's pages. An API from before the collection (migration 085)
 * 404s `/partner/hosted-pages`; its one page, if any, comes from the old route.
 */
export async function loadHostedPages(api: PartnerApi): Promise<HostedPageList> {
  try {
    return await api.listHostedPages();
  } catch (err) {
    if (!(err instanceof ApiError && err.status === 404)) throw err;
    const one = await hostedPageOrNull(() => api.getHostedPage());
    return { items: one ? [one] : [], limit: 1, used: one ? 1 : 0 };
  }
}

/** The organisation's live, enabled sites: the ones a page can show. */
async function liveSites(api: PartnerApi): Promise<SiteSummary[]> {
  const page = await api.listSites();
  return page.items.filter((s) => s.environment === 'live' && s.enabled);
}

function issuesFor(error: ApiError | null, path: string): FieldIssue[] {
  return error?.fields.filter((f) => f.path === path) ?? [];
}

function counter(value: string, max: number) {
  return `${value.length}/${max} characters`;
}

/** Headline and intro inputs, shared by the create form and the editor. */
function CopyFields({
  headline,
  intro,
  setHeadline,
  setIntro,
  error,
}: {
  headline: string;
  intro: string;
  setHeadline: (v: string) => void;
  setIntro: (v: string) => void;
  error: ApiError | null;
}) {
  return (
    <>
      <Field
        label="Headline"
        optional
        hint={`${counter(headline, HOSTED_PAGE_HEADLINE_MAX)}. Leave it empty for our default headline.`}
        issues={issuesFor(error, 'headline')}
      >
        {(p) => (
          <input
            {...p}
            className="input"
            maxLength={HOSTED_PAGE_HEADLINE_MAX}
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            placeholder="Something broken at home? Show us."
          />
        )}
      </Field>
      <Field
        label="Intro"
        optional
        hint={`${counter(intro, HOSTED_PAGE_INTRO_MAX)}. Plain text, shown under the headline.`}
        issues={issuesFor(error, 'intro')}
      >
        {(p) => (
          <textarea
            {...p}
            className="input"
            rows={4}
            maxLength={HOSTED_PAGE_INTRO_MAX}
            value={intro}
            onChange={(e) => setIntro(e.target.value)}
            placeholder="Snap a photo of the problem for an instant diagnosis, or request a quote and we’ll reply the same day."
          />
        )}
      </Field>
    </>
  );
}

/** Which live site a page shows. Only offered when there is more than one. */
function SitePicker({ sites, value, onChange }: { sites: SiteSummary[]; value: string; onChange: (v: string) => void }) {
  if (sites.length < 2) return null;
  return (
    <Field label="Site" hint="The page runs this site’s widget, with its published branding.">
      {(p) => (
        <select {...p} className="input" value={value} onChange={(e) => onChange(e.target.value)}>
          {sites.map((s) => (
            <option key={s.site_key} value={s.site_key}>
              {s.label || s.site_key}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

function CreateForm({
  first,
  onboarding = false,
  sites,
  onCreated,
  onCancel,
}: {
  first: boolean;
  /** Mounted on onboarding screen 3, where "the next steps" is where leads go. */
  onboarding?: boolean;
  sites: SiteSummary[];
  onCreated: (page: HostedPage) => void;
  onCancel?: () => void;
}) {
  const { api, org } = useSession();
  const toast = useToast();
  const [slug, setSlug] = useState('');
  const [headline, setHeadline] = useState('');
  const [intro, setIntro] = useState('');
  const [siteKey, setSiteKey] = useState(sites[0]?.site_key ?? '');
  const create = useMutation((body: HostedPageCreateBody) => api.createHostedPage(body));
  const suggested = suggestSlug(org?.display_name ?? '');
  const slugValue = slug.trim().toLowerCase();
  const slugInvalid = slugValue !== '' && !HOSTED_PAGE_SLUG_RE.test(slugValue);
  const slugIssues: FieldIssue[] = slugInvalid
    ? [{ path: 'slug', message: 'Use lower-case letters, numbers and hyphens, starting and ending with a letter or number.' }]
    : create.error?.code === 'SLUG_TAKEN'
      ? [{ path: 'slug', message: 'That address is taken. Try another.' }]
      : issuesFor(create.error, 'slug');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const created = await create.run({
      ...(slugValue ? { slug: slugValue } : {}),
      ...(sites.length > 1 && siteKey ? { site_key: siteKey } : {}),
      ...(headline.trim() ? { headline: headline.trim() } : {}),
      ...(intro.trim() ? { intro: intro.trim() } : {}),
    });
    if (created) {
      onCreated(created);
      toast(created.served ? 'Your page is live' : 'Your page is on, but it isn’t showing yet');
    }
  };

  return (
    <form className="stack" onSubmit={submit} noValidate aria-label={first ? 'Create your page' : 'Add a page'}>
      {first ? (
        <p style={{ margin: 0 }}>
          We host a page for your business at <strong>finderlyfix.com/p/…</strong>: your details, the photo diagnosis and a quote form, in
          your branding. Leads from it go wherever you choose {onboarding ? 'in the next steps' : 'under Where leads go'}. Nothing to install.
        </p>
      ) : (
        <p style={{ margin: 0 }}>A second or third page gets its own link and QR code — for another brand, service area or campaign.</p>
      )}
      <Field
        label="Page address"
        optional
        hint={
          <>
            Leave it empty to use <span className="mono">finderlyfix.com/p/{suggested}</span>
            {first ? '' : ' (or the next free variant)'}. Lower-case letters, numbers and hyphens, up to {HOSTED_PAGE_SLUG_MAX} characters.{' '}
            <strong>You can’t change the address after you create the page</strong> — it’s the link you print and share.
          </>
        }
        issues={slugIssues}
      >
        {(p) => (
          <div className="keyline" style={{ alignItems: 'center' }}>
            <span className="mono small muted" aria-hidden="true">
              finderlyfix.com/p/
            </span>
            <input
              {...p}
              className="input mono"
              style={{ flex: '1 1 auto', minWidth: 0 }}
              maxLength={HOSTED_PAGE_SLUG_MAX}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder={suggested}
            />
          </div>
        )}
      </Field>
      <SitePicker sites={sites} value={siteKey} onChange={setSiteKey} />
      <CopyFields headline={headline} intro={intro} setHeadline={setHeadline} setIntro={setIntro} error={create.error} />
      <div className="row" style={{ flexWrap: 'wrap' }}>
        <button type="submit" className="btn btn-primary" disabled={create.pending || slugInvalid}>
          {create.pending ? 'Creating…' : first ? 'Create my page' : 'Create page'}
        </button>
        {onCancel ? (
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
      </div>
      {create.error?.code === 'SLUG_TAKEN' ? null : <ErrorNotice error={create.error} />}
    </form>
  );
}

function Editor({ page, sites, onSaved }: { page: HostedPage; sites: SiteSummary[]; onSaved: (page: HostedPage) => void }) {
  const { api } = useSession();
  const toast = useToast();
  const [headline, setHeadline] = useState(page.headline ?? '');
  const [intro, setIntro] = useState(page.intro ?? '');
  const [siteKey, setSiteKey] = useState(page.site_key);
  // A fresh page from the server (another tab's save, a reload) resets the
  // form. Done during render, not in an effect: a mount-time effect ran after
  // the fields were on screen and could wipe an edit made before it flushed.
  const savedKey = JSON.stringify([page.headline, page.intro, page.site_key]);
  const [seenKey, setSeenKey] = useState(savedKey);
  if (seenKey !== savedKey) {
    setSeenKey(savedKey);
    setHeadline(page.headline ?? '');
    setIntro(page.intro ?? '');
    setSiteKey(page.site_key);
  }
  const save = useMutation((patch: { headline?: string; intro?: string; site_key?: string }) => api.updateHostedPage(page.slug, patch));
  const h = headline.trim();
  const i = intro.trim();
  const patch = {
    ...(h !== (page.headline ?? '') ? { headline: h } : {}),
    ...(i !== (page.intro ?? '') ? { intro: i } : {}),
    ...(siteKey !== page.site_key ? { site_key: siteKey } : {}),
  };
  const dirty = Object.keys(patch).length > 0;
  // A page on a key that is no longer live and enabled still shows it as an option.
  const options = sites.some((s) => s.site_key === page.site_key)
    ? sites
    : [...sites, { site_key: page.site_key, label: page.site_key } as SiteSummary];

  return (
    <form
      className="stack"
      aria-label={`Edit ${page.url}`}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!dirty) return;
        // An empty string clears the field; the page falls back to default copy.
        const saved = await save.run(patch);
        if (saved) {
          onSaved(saved);
          toast('Saved. Your page shows the change within a minute.');
        }
      }}
    >
      <SitePicker sites={options} value={siteKey} onChange={setSiteKey} />
      <CopyFields headline={headline} intro={intro} setHeadline={setHeadline} setIntro={setIntro} error={save.error} />
      <div className="row" style={{ flexWrap: 'wrap' }}>
        <button type="submit" className="btn btn-primary" disabled={!dirty || save.pending}>
          {save.pending ? 'Saving…' : 'Save changes'}
        </button>
        {dirty ? (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setHeadline(page.headline ?? '');
              setIntro(page.intro ?? '');
              setSiteKey(page.site_key);
              save.reset();
            }}
          >
            Discard
          </button>
        ) : null}
      </div>
      <ErrorNotice error={save.error} />
    </form>
  );
}

/** One page: its link and QR code, on/off, the editor, and Remove. */
function PageCard({
  page,
  sites,
  onChanged,
  onRemoved,
}: {
  page: HostedPage;
  sites: SiteSummary[];
  onChanged: (page: HostedPage) => void;
  onRemoved: (slug: string) => void;
}) {
  const { api, role } = useSession();
  const toast = useToast();
  const headingId = useId();
  const [confirming, setConfirming] = useState(false);
  const toggle = useMutation((enabled: boolean) => api.updateHostedPage(page.slug, { enabled }));
  const remove = useMutation(async () => {
    await api.removeHostedPage(page.slug);
    return true;
  });
  const path = page.url.replace(/^https?:\/\//, '');

  return (
    <article className="panel panel-pad stack" aria-labelledby={headingId}>
      <h3 id={headingId} className="mono wrap-anywhere" style={{ margin: 0 }}>
        {path}
      </h3>
      {page.enabled && page.served ? (
        <>
          <WidgetPageLink page={page} />
          <p className="small muted" style={{ margin: 0 }}>
            Share this link, print the QR code on a flyer, or put it in your email signature. The address can’t be changed. The page uses
            your branding once you publish it, and search engines are asked not to index it until then. Created <Time iso={page.created_at} />.
          </p>
        </>
      ) : page.enabled ? (
        <div className="notice warn" role="status">
          <div className="stack-sm" style={{ gap: 2 }}>
            <strong>This page is switched on, but it isn’t showing yet.</strong>
            <span>
              {page.url} goes live once its live site key is enabled on Sites &amp; keys (and while your account isn’t suspended). Until
              then visitors see a “page not found”.
            </span>
          </div>
        </div>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          Switched off: visitors see “page not found”. The address stays yours.
        </p>
      )}
      {/* Contractor pages phase 1: phone, services, hours and photos live on /pages/:slug. */}
      {can(role, 'hostedPageProfile') ? <PageCompletenessLink slug={page.slug} enabled={page.enabled} /> : null}
      <Switch
        label={`Page on (${path})`}
        description={page.enabled ? 'Anyone with the link can open it.' : 'Turn it on to make the link work again.'}
        checked={page.enabled}
        disabled={toggle.pending}
        onChange={async (enabled) => {
          const saved = await toggle.run(enabled);
          if (saved) {
            onChanged(saved);
            toast(enabled ? 'Page turned on' : 'Page turned off');
          }
        }}
      />
      <ErrorNotice error={toggle.error} />
      <hr className="divider" />
      <Editor page={page} sites={sites} onSaved={onChanged} />
      <hr className="divider" />
      {confirming ? (
        <div className="stack-sm" role="group" aria-label={`Remove ${path}?`}>
          <p className="small" style={{ margin: 0 }}>
            Remove this page? Its link stops working and it leaves this list, which frees a place. Nothing is deleted, and the address stays
            yours: create a page with the same address to bring it back.
          </p>
          <div className="row" style={{ flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              disabled={remove.pending}
              onClick={async () => {
                if (await remove.run()) {
                  onRemoved(page.slug);
                  toast('Page removed');
                }
              }}
            >
              {remove.pending ? 'Removing…' : 'Remove page'}
            </button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setConfirming(false)}>
              Keep it
            </button>
          </div>
          <ErrorNotice error={remove.error} />
        </div>
      ) : (
        <div>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setConfirming(true)}>
            Remove {path}
          </button>
        </div>
      )}
    </article>
  );
}

export function HostedPagePanel({ onPagesChange, onboarding = false }: { onPagesChange?: (pages: HostedPage[]) => void; onboarding?: boolean } = {}) {
  const { api } = useSession();
  const list = useQuery(() => loadHostedPages(api), []);
  // Onboarding screen 3 offers "Next: complete your page" once one is live.
  const notify = useRef(onPagesChange);
  notify.current = onPagesChange;
  useEffect(() => {
    if (list.data) notify.current?.(list.data.items);
  }, [list.data]);
  const sites = useQuery(() => liveSites(api), []);
  const [adding, setAdding] = useState(false);
  const reasonId = useId();
  const items = list.data?.items ?? [];
  const limit = list.data?.limit ?? HOSTED_PAGES_MAX;
  const full = items.length >= limit;
  const live = sites.data ?? [];

  const setItems = (next: HostedPage[]) => list.setData({ items: next, limit, used: next.length });
  const upsert = (page: HostedPage) =>
    setItems(items.some((p) => p.slug === page.slug) ? items.map((p) => (p.slug === page.slug ? page : p)) : [...items, page]);

  return (
    <section className="stack" aria-label="Your pages">
      {list.loading ? (
        <Loading label="Checking for your pages" />
      ) : list.error ? (
        <ErrorNotice error={list.error} />
      ) : items.length === 0 ? (
        <div className="panel panel-pad stack">
          <CreateForm first onboarding={onboarding} sites={live} onCreated={(page) => upsert(page)} />
        </div>
      ) : (
        <>
          <p className="small muted" style={{ margin: 0 }}>
            {items.length} of {limit} {limit === 1 ? 'page' : 'pages'} used. Each gets its own link and QR code — e.g. one per brand,
            service area or campaign.
          </p>
          {items.map((page) => (
            <PageCard
              key={page.slug}
              page={page}
              sites={live}
              onChanged={upsert}
              onRemoved={(slug) => setItems(items.filter((p) => p.slug !== slug))}
            />
          ))}
          {adding && !full ? (
            <div className="panel panel-pad stack">
              <CreateForm
                first={false}
                sites={live}
                onCancel={() => setAdding(false)}
                onCreated={(page) => {
                  upsert(page);
                  setAdding(false);
                }}
              />
            </div>
          ) : (
            <div className="row" style={{ flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn"
                disabled={full}
                aria-describedby={full ? reasonId : undefined}
                onClick={() => setAdding(true)}
              >
                Add a page
              </button>
              {full ? (
                <span id={reasonId} className="small muted">
                  You have {limit} of {limit} pages. Remove one to add another.
                </span>
              ) : null}
            </div>
          )}
        </>
      )}
    </section>
  );
}
